/**
 * Cross-tab identity guard (#2439 simplification, integrator ruling
 * 2026-10-09).
 *
 * On web every tab of a browser shares ONE refresh cookie, but each tab holds
 * its own idea of who is signed in. When one tab signs in, signs out or adopts
 * a different user, the others would keep showing, and requesting as, the
 * previous person. So the tab that changed broadcasts
 * `{ type: 'auth-changed', userId }` on `BroadcastChannel('trf-auth')`, or
 * through a `localStorage` write when BroadcastChannel is missing. A tab that
 * holds a different user (or holds someone while the message says nobody)
 * hard-reloads: to its current path when the message names a user, to
 * /login when it names nobody.
 *
 * The id a tab holds is its SESSION OWNER: the signed-in account, or during
 * an impersonation the operator, never the impersonated family. An
 * impersonation in one tab is not another tab's business.
 *
 * `services/api.ts` reads the same owner: a 401 refresh that returns another
 * user's token is never stored or replayed (see the refresh interceptor).
 *
 * Every storage and channel access is wrapped: private windows, blocked site
 * data and old browsers must still render.
 */
import { hardReload } from './hard-reload';
import { forgetSignedInTab } from './return-path';

export const AUTH_SYNC_CHANNEL = 'trf-auth';
export const SYNC_STORAGE_KEY_FOR_IDENTITY = 'trf-auth-changed';

type AuthChanged = { type: 'auth-changed'; userId: string | null };

let owner: string | null = null;
let channel: BroadcastChannel | null | undefined;

/** The user id this tab holds, or null when it holds nobody. */
export function tabOwner(): string | null {
  return owner;
}

/** Record who this tab holds. AuthContext is the one writer. */
export function setTabOwner(userId: string | null): void {
  owner = userId;
}

function getChannel(): BroadcastChannel | null {
  if (channel !== undefined) return channel;
  try {
    channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(AUTH_SYNC_CHANNEL);
    // Node (tests) keeps the process alive for an open channel.
    (channel as unknown as { unref?: () => void } | null)?.unref?.();
  } catch {
    channel = null;
  }
  return channel;
}

/** Tell the other tabs who this tab holds now. Never throws. */
export function announceAuthChange(userId: string | null): void {
  const message: AuthChanged = { type: 'auth-changed', userId };
  const ch = getChannel();
  if (ch) {
    try {
      ch.postMessage(message);
      return;
    } catch {
      // Fall through to storage.
    }
  }
  try {
    // `at` makes every write a change, so the storage event always fires.
    localStorage.setItem(SYNC_STORAGE_KEY_FOR_IDENTITY, JSON.stringify({ ...message, at: Date.now() }));
  } catch {
    // Storage blocked: the other tabs find out on their next request.
  }
}

function readMessage(data: unknown): AuthChanged | null {
  if (!data || typeof data !== 'object') return null;
  const m = data as { type?: unknown; userId?: unknown };
  if (m.type !== 'auth-changed') return null;
  if (m.userId !== null && typeof m.userId !== 'string') return null;
  return { type: 'auth-changed', userId: m.userId };
}

/** Reload this tab, the way a cross-tab message naming `userId` does. */
export function reloadForIdentity(userId: string | null): void {
  // #2429: a sign-out in another tab of this browser is deliberate, so the
  // reloaded /login must not read it as a session that ended under her.
  if (!userId) forgetSignedInTab();
  hardReload(userId ? null : '/login');
}

/**
 * Listen for other tabs' changes until the returned function is called.
 * `isBooting`: while the boot session check runs, the tab holds nobody yet
 * and will adopt whatever the shared cookie says, so messages are ignored.
 */
export function startAuthSync({ isBooting }: { isBooting: () => boolean }): () => void {
  const onMessage = (msg: AuthChanged | null) => {
    if (!msg || isBooting()) return;
    if (msg.userId !== owner) reloadForIdentity(msg.userId);
  };
  const onChannel = (e: MessageEvent) => onMessage(readMessage(e.data));
  const onStorage = (e: StorageEvent) => {
    if (e.key !== SYNC_STORAGE_KEY_FOR_IDENTITY || !e.newValue) return;
    try {
      onMessage(readMessage(JSON.parse(e.newValue)));
    } catch {
      // Not ours, or unreadable.
    }
  };
  const ch = getChannel();
  try { ch?.addEventListener('message', onChannel); } catch { /* no channel */ }
  window.addEventListener('storage', onStorage);
  return () => {
    try { ch?.removeEventListener('message', onChannel); } catch { /* no channel */ }
    window.removeEventListener('storage', onStorage);
  };
}

/**
 * The `id` claim of an access token's JWT payload, or null. Decoded only, not
 * verified: the server verifies; this only compares identities.
 */
function claimsOf(token: string): { id?: unknown; impersonated_by?: unknown } | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(padded + '='.repeat((4 - (padded.length % 4)) % 4)));
  } catch {
    return null;
  }
}

const idClaim = (v: unknown): string | null => (v === undefined || v === null || v === '' ? null : String(v));

export function userIdOfToken(token: string): string | null {
  return idClaim(claimsOf(token)?.id);
}

/**
 * The session owner a token speaks for: the operator for an impersonation
 * token, the account itself otherwise. Comparable with `tabOwner()`.
 */
export function ownerOfToken(token: string): string | null {
  const claims = claimsOf(token);
  return idClaim(claims?.impersonated_by) ?? idClaim(claims?.id);
}

/** True when the token is an impersonation token (it names an operator). */
export function isImpersonationToken(token: string): boolean {
  return idClaim(claimsOf(token)?.impersonated_by) !== null;
}

/**
 * #2478: the window event `services/api.ts` raises when a request sent with an
 * impersonation token comes back 401. The token expired, or the staff
 * session it is bound to ended. It has no refresh token of its own, and
 * refreshing from the operator's cookie would replay the request as the
 * operator under the family's banner, so the interceptor raises this instead
 * and AuthContext ends the impersonation.
 */
export const IMPERSONATION_ENDED_EVENT = 'auth:impersonation-ended';
